import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAccessTier } from "@/hooks/useAccessTier";
import { motion } from "framer-motion";
import {
  Map as MapIcon,
  Sparkles,
  ArrowRight,
  BookOpen,
  Trophy,
  Layers,
} from "lucide-react";
import { useCurriculumProgress } from "@/hooks/useCurriculumProgress";
import { useNextLesson } from "@/hooks/useNextLesson";
import { getCoursePhaseConfig, isCourseAccessible } from "@/lib/curriculumPhases";
import PageShell from "@/components/premium/PageShell";
import PageHeader from "@/components/premium/PageHeader";
import PremiumCard from "@/components/premium/PremiumCard";
import StatCard from "@/components/premium/StatCard";
import { getCoursePillar, PILLAR_META, Pillar } from "@/components/premium/PillarBadge";
import LockedCourseModal from "@/components/curriculum/LockedCourseModal";
import SEO from "@/components/SEO";
import MilestoneCard from "@/components/curriculum/MilestoneCard";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type PillarFilter = "all" | Pillar;

const PILLAR_ORDER: Pillar[] = ["finance", "economics", "entrepreneurship"];

const PHASE_ACCENT: Record<number, string> = {
  1: "text-primary",
  2: "text-accent",
  3: "text-secondary",
  4: "text-primary",
  5: "text-accent",
  6: "text-secondary",
};

const CurriculumMap = () => {
  const navigate = useNavigate();
  const { tier, loading: tierLoading } = useAccessTier();
  // Paid product: there is no restricted customer tier. AccessGate already
  // routes unpaid accounts to the subscription path before this screen renders.

  const { getPhases, courseProgress, loading } = useCurriculumProgress();
  const { data: nextLesson } = useNextLesson();
  const [modalCourse, setModalCourse] = useState<{ name: string; phase: number } | null>(null);
  const [pillar, setPillar] = useState<PillarFilter>("all");

  const phases = useMemo(() => getPhases(), [getPhases]);

  // Aggregate progress for the summary strip.
  const totals = useMemo(() => {
    const t = courseProgress.reduce(
      (acc, c) => {
        acc.lessons += c.totalLessons;
        acc.completedLessons += c.completedLessons;
        if (c.isComplete) acc.coursesDone += 1;
        if (isCourseAccessible(c.courseName)) acc.coursesLive += 1;
        return acc;
      },
      { lessons: 0, completedLessons: 0, coursesDone: 0, coursesLive: 0 },
    );
    return t;
  }, [courseProgress]);

  if (loading || tierLoading) {
    return (
      <PageShell>
        <div className="flex min-h-[40vh] items-center justify-center">
          <div className="w-5 h-5 rounded-full border-2 border-primary border-t-transparent animate-spin" />
        </div>
      </PageShell>
    );
  }

  const modalConfig = modalCourse ? getCoursePhaseConfig(modalCourse.name) : null;
  const currentPillar: Pillar | null = nextLesson ? getCoursePillar(nextLesson.courseName) : null;

  return (
    <PageShell>
      <SEO
        title="Curriculum Roadmap — HeirWise"
        description="Explore the HeirWise roadmap across Finance, Economics, and Entrepreneurship. See unlocked modules and your next lesson."
        path="/curriculum"
      />
      <PageHeader
        title="Roadmap"
        subtitle="See your path through finance, economics, and entrepreneurship."
        icon={MapIcon}
      />

      {/* Current Milestone hero */}
      {nextLesson && currentPillar && (
        <CurrentMilestoneHero
          courseName={nextLesson.courseName}
          lessonName={nextLesson.lessonName}
          lessonNumber={nextLesson.lessonNumber}
          totalLessons={nextLesson.totalLessons}
          pillar={currentPillar}
          onContinue={() => navigate(`/lesson/${nextLesson.lessonId}`)}
        />
      )}

      {/* Summary stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard
          label="Lessons"
          value={`${totals.completedLessons}/${totals.lessons}`}
          icon={BookOpen}
        />
        <StatCard
          label="Courses Done"
          value={String(totals.coursesDone)}
          icon={Trophy}
        />
        <StatCard
          label="Live Paths"
          value={String(totals.coursesLive)}
          icon={Layers}
        />
        <StatCard
          label="Current Pillar"
          value={currentPillar ? PILLAR_META[currentPillar].label : "—"}
          icon={Sparkles}
        />
      </div>

      {/* Pillar filter */}
      <div className="flex flex-wrap items-center gap-2">
        <PillarPill
          active={pillar === "all"}
          onClick={() => setPillar("all")}
          label="All Pillars"
          tone="text-foreground"
          dotClass="bg-foreground/60"
        />
        {PILLAR_ORDER.map((p) => (
          <PillarPill
            key={p}
            active={pillar === p}
            onClick={() => setPillar(p)}
            label={PILLAR_META[p].label}
            sub={PILLAR_META[p].instructor}
            tone={PILLAR_META[p].tone}
            dotClass={
              p === "finance"
                ? "bg-primary"
                : p === "economics"
                ? "bg-accent"
                : "bg-secondary"
            }
          />
        ))}
      </div>

      {/* Phase sections */}
      {phases.map((phase, pi) => {
        const filteredCourses = phase.courses.filter((c) => {
          if (pillar === "all") return true;
          return getCoursePillar(c.courseName) === pillar;
        });

        if (filteredCourses.length === 0) return null;

        return (
          <motion.section
            key={phase.phaseNumber}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: pi * 0.05 }}
            className="space-y-3"
          >
            <div className="flex items-baseline gap-3">
              <span
                className={cn(
                  "text-[11px] font-heading font-bold uppercase tracking-[0.18em]",
                  PHASE_ACCENT[phase.phaseNumber] || "text-foreground",
                )}
              >
                Phase {phase.phaseNumber}
              </span>
              <span className="text-xs text-muted-foreground">— {phase.label}</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {filteredCourses.map((course) => {
                const accessible = isCourseAccessible(course.courseName);
                const config = getCoursePhaseConfig(course.courseName);
                const cp = getCoursePillar(course.courseName);
                const isCurrent =
                  nextLesson && nextLesson.courseName === course.courseName;

                let status: "completed" | "in_progress" | "not_started" | "locked";
                if (!accessible) status = "locked";
                else if (course.isComplete) status = "completed";
                else if (course.completedLessons > 0 || isCurrent) status = "in_progress";
                else status = "not_started";

                return (
                  <MilestoneCard
                    key={course.courseName}
                    title={course.courseName}
                    pillar={cp}
                    completed={course.completedLessons}
                    total={course.totalLessons}
                    status={status}
                    lockedConfig={!accessible ? config : undefined}
                    onClick={() =>
                      accessible
                        ? navigate(`/course/${encodeURIComponent(course.courseName)}`)
                        : setModalCourse({ name: course.courseName, phase: phase.phaseNumber })
                    }
                  />
                );
              })}
            </div>
          </motion.section>
        );
      })}

      {modalCourse && modalConfig && (
        <LockedCourseModal
          open={!!modalCourse}
          onOpenChange={(open) => !open && setModalCourse(null)}
          courseName={modalCourse.name}
          config={modalConfig}
          phase={modalCourse.phase}
        />
      )}
    </PageShell>
  );
};

/* ---------- Local helpers ---------- */

function CurrentMilestoneHero({
  courseName,
  lessonName,
  lessonNumber,
  totalLessons,
  pillar,
  onContinue,
}: {
  courseName: string;
  lessonName: string;
  lessonNumber: number;
  totalLessons: number;
  pillar: Pillar;
  onContinue: () => void;
}) {
  const meta = PILLAR_META[pillar];
  const pct = Math.round(((lessonNumber - 1) / Math.max(1, totalLessons)) * 100);

  return (
    <PremiumCard variant="glow" className="relative overflow-hidden">
      <div
        aria-hidden
        className={cn(
          "absolute -top-16 -right-10 h-48 w-48 rounded-full blur-3xl opacity-40",
          pillar === "finance"
            ? "bg-primary"
            : pillar === "economics"
            ? "bg-accent"
            : "bg-secondary",
        )}
      />
      <div className="relative flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            <span className={cn("text-[10px] font-heading font-bold uppercase tracking-[0.18em]", meta.tone)}>
              Current Milestone
            </span>
            <span className="text-[10px] text-muted-foreground">·</span>
            <span className="text-[10px] text-muted-foreground">
              Guided by {meta.instructor}
            </span>
          </div>
          <h2 className="font-heading font-bold text-lg sm:text-xl text-foreground leading-tight">
            {courseName}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground line-clamp-1">
            Next up: <span className="text-foreground/90">{lessonName}</span>
          </p>
          <div className="mt-3 flex items-center gap-3">
            <div className="flex-1 h-1.5 rounded-full bg-muted/60 overflow-hidden max-w-xs">
              <div
                className={cn(
                  "h-full rounded-full transition-all",
                  pillar === "finance"
                    ? "bg-primary"
                    : pillar === "economics"
                    ? "bg-accent"
                    : "bg-secondary",
                )}
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="text-[11px] font-mono text-muted-foreground shrink-0">
              Lesson {lessonNumber}/{totalLessons}
            </span>
          </div>
        </div>
        <Button
          onClick={onContinue}
          size="lg"
          className="shrink-0 w-full sm:w-auto bg-gradient-to-r from-primary to-secondary text-primary-foreground shadow-[0_0_24px_hsl(var(--primary)/0.35)] hover:shadow-[0_0_32px_hsl(var(--primary)/0.5)]"
        >
          Continue Lesson
          <ArrowRight className="w-4 h-4 ml-1.5" />
        </Button>
      </div>
    </PremiumCard>
  );
}

function PillarPill({
  active,
  onClick,
  label,
  sub,
  tone,
  dotClass,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  sub?: string;
  tone: string;
  dotClass: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition-all",
        active
          ? "border-foreground/40 bg-card/90 text-foreground shadow-[0_0_0_3px_hsl(var(--background))_inset]"
          : "border-border/50 bg-card/40 text-muted-foreground hover:text-foreground hover:border-border",
      )}
    >
      <span className={cn("w-1.5 h-1.5 rounded-full", dotClass)} />
      <span className={cn(active ? tone : "")}>{label}</span>
      {sub && (
        <span className="text-[10px] font-normal text-muted-foreground/80 normal-case">
          · {sub}
        </span>
      )}
    </button>
  );
}

export default CurriculumMap;
